/* Media policy utils to control Cloudinary usage in dev/stage/prod.
   Defaults: block Cloudinary on dev-like hosts; allow on prod.
   Overrides:
   - Query:  ?media=cloud | ?media=nocloud | ?media=economy
   - LocalStorage:
       MEDIA_ALLOW_CLOUD = '1'           // allow in dev
       MEDIA_POLICY      = 'economy'     // prefer low-cost transforms in prod
       MEDIA_CLOUD_HOSTS = 'host1,host2' // add CNAMEs or extra hosts
   - Global/ENV (optional):
       window.__MEDIA_CLOUD_HOSTS = 'cdn.example.com,res.cloudinary.com'
       REACT_APP_MEDIA_CLOUD_HOSTS / VITE_MEDIA_CLOUD_HOSTS / NEXT_PUBLIC_MEDIA_CLOUD_HOSTS
*/

const DEV_HOST_RE = /(localhost|127\.0\.0\.1|^\[?::1\]?|\.local)$/i;

export function isDevLike() {
  try {
    const { hostname, port } = window.location;
    const envIsDev =
      typeof process !== 'undefined' &&
      process.env &&
      process.env.NODE_ENV !== 'production';

    // Private networks and common dev ports look like dev too
    const privateIp =
      /^10\./.test(hostname) ||
      /^192\.168\./.test(hostname) ||
      /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname) ||
      /^169\.254\./.test(hostname);

    const devPort = !!port && !/^(80|443)$/.test(port);

    return envIsDev || DEV_HOST_RE.test(hostname) || privateIp || devPort;
  } catch {
    return true; // safer default: treat as dev if detection fails
  }
}

function readConfiguredHosts() {
  const out = new Set(['res.cloudinary.com']); // always include default
  try {
    const fromLS = (window.localStorage?.getItem('MEDIA_CLOUD_HOSTS') || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    for (const h of fromLS) out.add(h.toLowerCase());
  } catch {}

  try {
    const g = window.__MEDIA_CLOUD_HOSTS;
    if (typeof g === 'string') {
      for (const h of g
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)) {
        out.add(h.toLowerCase());
      }
    }
  } catch {}

  const envs = [
    process?.env?.REACT_APP_MEDIA_CLOUD_HOSTS,
    process?.env?.VITE_MEDIA_CLOUD_HOSTS,
    process?.env?.NEXT_PUBLIC_MEDIA_CLOUD_HOSTS,
  ].filter(Boolean);
  for (const e of envs) {
    for (const h of String(e)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)) {
      out.add(h.toLowerCase());
    }
  }
  return out;
}

const CLOUD_HOSTS = readConfiguredHosts();

export function getQueryParam(name) {
  try {
    const sp = new URLSearchParams(window.location.search || '');
    return sp.get(name) || null;
  } catch {
    return null;
  }
}

// Detect Cloudinary URLs, including custom CNAMEs.
// Works for:
//   https://res.cloudinary.com/<cloud>/image/upload/v.../file.png
//   https://cdn.example.com/image/upload/... (CNAME)
//   https://res.cloudinary.com/<cloud>/image/fetch/... (fetch)
//   video/upload|fetch too
export function isCloudinaryUrl(url) {
  if (typeof url !== 'string' || !url) return false;
  try {
    const u = new URL(url, window.location.origin);
    const host = u.hostname.toLowerCase();

    // Host match: known cloudinary hosts or custom CNAMEs provided via config
    const hostMatches = host.includes('cloudinary') || CLOUD_HOSTS.has(host);

    // Path match: typical Cloudinary resource paths
    const p = u.pathname.toLowerCase();
    const pathMatches =
      /\/(image|video)\/(upload|fetch)\//.test(p) || /\/raw\/upload\//.test(p);

    // If host is known cloud host, accept even if path parsing differs
    // console.log('cloudinary check:', url, {
    //   host,
    //   path: u.pathname,
    //   hostMatches,
    //   pathMatches,
    // });
    return hostMatches && (pathMatches || host.includes('cloudinary'));
  } catch {
    return false;
  }
}

export function shouldBlockCloudinary() {
  const qp = (getQueryParam('media') || '').toLowerCase();
  if (qp === 'cloud') return false;
  if (qp === 'nocloud') return true;

  // Default: block on dev-like unless explicitly allowed
  const dev = isDevLike();
  try {
    const allow = window.localStorage?.getItem('MEDIA_ALLOW_CLOUD') === '1';
    if (dev) return !allow;
  } catch {}
  return false; // allow on prod by default
}

export function preferEconomyTransforms() {
  const qp = (getQueryParam('media') || '').toLowerCase();
  if (qp === 'economy') return true;
  try {
    if (window.localStorage?.getItem('MEDIA_POLICY') === 'economy') return true;
  } catch {}
  return false;
}

// Inject low-cost transforms for Cloudinary (upload|fetch)
export function economyCloudinaryUrl(url) {
  if (!isCloudinaryUrl(url)) return url;
  // Skip if transforms already present
  if (/(?:,|\/)(f_auto|q_auto(?::\w+)?|q_\d+)\b/i.test(url)) return url;

  // Insert after /image/upload/ or /image/fetch/
  return url
    .replace(/\/image\/upload\//i, '/image/upload/f_auto,q_auto:eco/')
    .replace(/\/image\/fetch\//i, '/image/fetch/f_auto,q_auto:eco/')
    .replace(/\/video\/upload\//i, '/video/upload/f_auto,q_auto:eco/')
    .replace(/\/video\/fetch\//i, '/video/fetch/f_auto,q_auto:eco/');
}

// Fast inline placeholder (no network)
const svgCache = new Map();
export function toPlaceholderSvgDataUri(text = 'Badge', size = 88) {
  const key = `${text}|${size}`;
  const cached = svgCache.get(key);
  if (cached) return cached;

  const hue = Math.abs(hashCode(text)) % 360;
  const bg = `hsl(${hue}, 60%, 18%)`;
  const fg = `hsl(${hue}, 85%, 68%)`;
  const initials = getInitials(text);

  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
  <defs>
    <radialGradient id="g" cx="30%" cy="30%" r="90%">
      <stop offset="0%" stop-color="${fg}" stop-opacity="0.9"/>
      <stop offset="100%" stop-color="${bg}" stop-opacity="1"/>
    </radialGradient>
  </defs>
  <rect rx="${Math.max(10, size * 0.18)}" ry="${Math.max(
    10,
    size * 0.18,
  )}" width="${size}" height="${size}" fill="url(#g)"/>
  <text x="50%" y="54%" dominant-baseline="middle" text-anchor="middle"
        font-family="Inter,system-ui,Segoe UI,Roboto,Helvetica,Arial,sans-serif"
        font-size="${Math.floor(
          size * 0.38,
        )}" font-weight="800" fill="rgba(0,0,0,.25)">${initials}</text>
</svg>`.trim();

  const dataUri = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  svgCache.set(key, dataUri);
  return dataUri;
}

export function resolveImageUrl(src, { alt = 'image', size = 88 } = {}) {
  const blocked = shouldBlockCloudinary() && isCloudinaryUrl(src);
  //   console.log('resolveImageUrl:', { src, alt, size, blocked });
  if (blocked) {
    return {
      src: toPlaceholderSvgDataUri(alt || 'image', size),
      blocked: true,
    };
  }
  const economy = preferEconomyTransforms();
  if (economy && isCloudinaryUrl(src)) {
    return { src: economyCloudinaryUrl(src), blocked: false };
  }
  return { src, blocked: false };
}

/* helpers */
function hashCode(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return h;
}
function getInitials(s) {
  const parts = String(s || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '•';
  const a = (parts[0][0] || '').toUpperCase();
  const b = (parts[1]?.[0] || '').toUpperCase();
  return (a + (b || '')).slice(0, 2);
}
